import {
  createClient,
  SupabaseClient,
  type SupportedStorage,
} from "@supabase/supabase-js";
import type { Database } from "../types/database";

export type WhistllSupabaseClient = SupabaseClient<Database>;

export interface SupabaseClientConfig {
  url: string;
  anonKey: string;
  /**
   * Optional override for auth persistence - defaults to true on the web,
   * should be disabled for short-lived server contexts (edge functions, tests).
   */
  persistSession?: boolean;
}

export interface SupabaseNativeClientConfig extends SupabaseClientConfig {
  /**
   * An async storage adapter - on React Native pass @react-native-async-storage/async-storage.
   * Needed because the browser `localStorage` Supabase uses by default does not exist in RN.
   */
  storage: SupportedStorage;
}

function assertKeys(url: string, anonKey: string) {
  if (!url || !anonKey) {
    throw new Error(
      "Supabase URL and anon key are required. Check your environment variables."
    );
  }
}

/**
 * Creates a browser/client-side Supabase client using the anon key.
 * Anon key is safe to expose - RLS policies are the security boundary.
 * Server-side code (edge functions) must use the service-role client factory instead.
 */
export function createBrowserClient(config: SupabaseClientConfig): WhistllSupabaseClient {
  const { url, anonKey, persistSession = true } = config;
  assertKeys(url, anonKey);

  return createClient<Database>(url, anonKey, {
    auth: {
      persistSession,
      autoRefreshToken: persistSession,
      detectSessionInUrl: persistSession,
    },
  });
}

/**
 * Creates a Supabase client for React Native. The key difference from the
 * browser client: session persistence goes through a caller-supplied async
 * storage adapter (AsyncStorage) because RN has no `window.localStorage`,
 * and `detectSessionInUrl` is disabled because there is no window URL to
 * parse magic-link tokens out of (deep links handle that flow instead).
 */
export function createNativeClient(
  config: SupabaseNativeClientConfig
): WhistllSupabaseClient {
  const { url, anonKey, storage, persistSession = true } = config;
  assertKeys(url, anonKey);

  return createClient<Database>(url, anonKey, {
    auth: {
      storage,
      persistSession,
      autoRefreshToken: persistSession,
      detectSessionInUrl: false,
    },
  });
}
